import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { fleetService } from '../../services/fleetService';
import { driverService } from '../../services/driverService';
import FleetStatusBadge from '../../components/fleets/FleetStatusBadge';
import DriverStatusBadge from '../../components/drivers/DriverStatusBadge';
import DataTable from '../../components/common/DataTable';
import {
    ChevronLeft,
    Edit,
    Users,
    MapPin,
    ChevronRight,
    DollarSign,
    AlertTriangle,
    Car,
    Clock,
    TrendingUp
} from 'lucide-react';

const FleetDetailPage = () => {
    const { fleetId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [fleet, setFleet] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [stats, setStats] = useState({
        total_drivers: 0,
        active_drivers: 0,
        total_orders: 0,
        total_revenue: 0,
        average_rating: 0
    });

    useEffect(() => {
        fetchFleetDetails();
        fetchFleetDrivers();
        fetchFleetStats();
    }, [fleetId]);

    const fetchFleetDetails = async () => {
        setIsLoading(true);
        try {
            const fleetData = await fleetService.getFleetById(fleetId);
            setFleet(fleetData);
        } catch (error) {
            console.error('Error fetching fleet details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleetDrivers = async () => {
        try {
            const { data } = await fleetService.getFleetDrivers(fleetId, { limit: 5 });
            setDrivers(data);
        } catch (error) {
            console.error('Error fetching fleet drivers:', error);
        }
    };

    const fetchFleetStats = async () => {
        try {
            const statsData = await fleetService.getFleetStats(fleetId);
            setStats(statsData);
        } catch (error) {
            console.error('Error fetching fleet stats:', error);
        }
    };

    const handleEditFleet = () => {
        navigate(`/fleets/${fleetId}/edit`);
    };

    const handleViewAllDrivers = () => {
        navigate(`/drivers?fleet_id=${fleetId}`);
    };

    const handleViewServiceArea = () => {
        navigate(`/fleets/${fleetId}/service-area`);
    };

    const handleStatusChange = async (status) => {
        try {
            await fleetService.setStatus(fleetId, status);
            fetchFleetDetails(); // 重新加載車隊詳情以更新狀態
        } catch (error) {
            console.error('Error updating fleet status:', error);
        }
    };

    const driverColumns = [
        {
            header: '姓名',
            accessor: 'full_name',
            cell: (row) => (
                <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-200 rounded-full mr-2 overflow-hidden">
                        {row.avatar_url && (
                            <img src={row.avatar_url} alt={row.full_name} className="w-full h-full object-cover" />
                        )}
                    </div>
                    <span>{row.full_name}</span>
                </div>
            )
        },
        {
            header: '車輛資訊',
            accessor: 'vehicle_model',
            cell: (row) => (
                <span>{row.vehicle_model} • {row.plate_number}</span>
            )
        },
        {
            header: '狀態',
            accessor: 'status',
            cell: (row) => (
                <DriverStatusBadge status={row.status} />
            )
        },
        {
            header: '評分',
            accessor: 'rating',
            cell: (row) => (
                <div className="flex items-center">
                    <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span>{row.rating ? row.rating.toFixed(1) : '-'}</span>
                </div>
            )
        },
        {
            header: '',
            accessor: 'action',
            cell: (row) => (
                <button
                    onClick={() => navigate(`/drivers/${row.driver_id}`)}
                    className="text-blue-500 hover:text-blue-700"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            )
        }
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!fleet) {
        return (
            <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-800 mb-2">找不到車隊</h2>
                <p className="text-gray-600 mb-4">無法找到ID為 {fleetId} 的車隊資訊。</p>
                <button
                    onClick={() => navigate('/fleets')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                    返回車隊列表
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center mb-6">
                <button
                    onClick={() => navigate('/fleets')}
                    className="p-2 mr-2 rounded-md hover:bg-gray-100"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold">車隊詳情</h1>
                <FleetStatusBadge status={fleet.status} className="ml-4" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 車隊基本資訊卡片 */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold">基本資訊</h3>
                            {user.user_type === 'admin' && (
                                <button
                                    onClick={handleEditFleet}
                                    className="text-blue-500 hover:text-blue-700"
                                >
                                    <Edit className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="mb-6">
                            <h4 className="text-xl font-bold">{fleet.fleet_name}</h4>
                            <p className="text-gray-600">ID: {fleet.fleet_id}</p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <h5 className="text-sm text-gray-500 mb-1">服務區域</h5>
                                <div className="flex items-center justify-between">
                                    <p>{fleet.city} {fleet.district}</p>
                                    <button
                                        onClick={handleViewServiceArea}
                                        className="text-blue-500 hover:text-blue-700"
                                        title="查看服務區域地圖"
                                    >
                                        <MapPin className="w-5 h-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{fleet.service_area}</p>
                            </div>

                            <div>
                                <h5 className="text-sm text-gray-500 mb-1">描述</h5>
                                <p>{fleet.description || '無描述'}</p>
                            </div>

                            <div>
                                <h5 className="text-sm text-gray-500 mb-1">建立時間</h5>
                                <p>{new Date(fleet.created_at).toLocaleDateString('zh-TW')}</p>
                            </div>
                        </div>

                        {user.user_type === 'admin' && (
                            <div className="mt-6 border-t border-gray-100 pt-4">
                                <h5 className="font-medium mb-3">營運狀態管理</h5>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => handleStatusChange('active')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${fleet.status === 'active'
                                                ? 'bg-green-500 text-white'
                                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        運營中
                                    </button>
                                    <button
                                        onClick={() => handleStatusChange('inactive')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${fleet.status === 'inactive'
                                                ? 'bg-gray-500 text-white'
                                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        暫停運營
                                    </button>
                                    <button
                                        onClick={() => handleStatusChange('maintenance')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${fleet.status === 'maintenance'
                                                ? 'bg-yellow-500 text-white'
                                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        系統維護
                                    </button>
                                    <button
                                        onClick={() => handleStatusChange('suspended')}
                                        className={`py-2 px-3 rounded-md text-sm font-medium ${fleet.status === 'suspended'
                                                ? 'bg-red-500 text-white'
                                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        停用車隊
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 統計數據與司機 */}
                <div className="lg:col-span-2 space-y-6">
                    {/* 統計卡片 */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-sm text-gray-500">總司機數</p>
                                <Users className="w-5 h-5 text-blue-500" />
                            </div>
                            <p className="text-2xl font-bold">{stats.total_drivers}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                活躍: {stats.active_drivers} ({Math.round(stats.active_drivers / stats.total_drivers * 100) || 0}%)
                            </p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-sm text-gray-500">本月訂單</p>
                                <Clock className="w-5 h-5 text-green-500" />
                            </div>
                            <p className="text-2xl font-bold">{stats.total_orders}</p>
                            <div className="flex items-center text-xs text-green-500 mt-1">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                <span>+{stats.order_growth || 0}%</span>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-sm text-gray-500">本月營收</p>
                                <DollarSign className="w-5 h-5 text-purple-500" />
                            </div>
                            <p className="text-2xl font-bold">${stats.total_revenue?.toLocaleString() || 0}</p>
                            <div className="flex items-center text-xs text-green-500 mt-1">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                <span>+{stats.revenue_growth || 0}%</span>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-sm text-gray-500">司機評分</p>
                                <Car className="w-5 h-5 text-yellow-500" />
                            </div>
                            <p className="text-2xl font-bold">{stats.average_rating?.toFixed(1) || 0}</p>
                            <div className="flex items-center text-xs text-gray-500 mt-1">
                                <span>共 {stats.total_ratings || 0} 筆評價</span>
                            </div>
                        </div>
                    </div>

                    {/* 計價資訊卡片 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100">
                            <h3 className="font-bold">計價資訊</h3>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                <div>
                                    <h5 className="text-sm text-gray-500 mb-1">基本車資</h5>
                                    <p className="font-medium">${fleet.base_fare} 元</p>
                                </div>

                                <div>
                                    <h5 className="text-sm text-gray-500 mb-1">每公里</h5>
                                    <p className="font-medium">${fleet.per_km_fare} 元</p>
                                </div>

                                <div>
                                    <h5 className="text-sm text-gray-500 mb-1">每分鐘</h5>
                                    <p className="font-medium">${fleet.per_minute_fare} 元</p>
                                </div>

                                <div>
                                    <h5 className="text-sm text-gray-500 mb-1">平台抽成</h5>
                                    <p className="font-medium">{fleet.commission_rate}%</p>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-md">
                                <h5 className="font-medium mb-2">車資計算方式</h5>
                                <p className="text-sm text-gray-600">
                                    總車資 = 基本車資(${fleet.base_fare}) + (距離 × 每公里車資) + (時間 × 每分鐘車資)
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* 車隊司機卡片 */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100">
                            <div className="flex justify-between items-center">
                                <h3 className="font-bold">車隊司機</h3>
                                <button
                                    onClick={handleViewAllDrivers}
                                    className="text-blue-500 text-sm hover:underline"
                                >
                                    查看全部
                                </button>
                            </div>
                        </div>

                        <div>
                            <DataTable
                                columns={driverColumns}
                                data={drivers}
                                emptyMessage="此車隊尚無司機"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FleetDetailPage;